import { Component, computed, ElementRef, input, output, viewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

export interface FileSelectResult {
  originalEvent: Event;
  currentFiles: File[];
  files: File[];
}

@Component({
  selector: 'kl-upload-file-basic',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './kl-upload-file-basic.html',
  styleUrl: './kl-upload-file-basic.scss',
})
export class KlUploadFileBasicComponent {
  private readonly nativeInput = viewChild<ElementRef<HTMLInputElement>>('nativeInput');

  readonly disabled = input(false);
  readonly maxFileSize = input(10);
  readonly acceptedFileExtentions = input('.pdf,.rar,.docx,image/*');
  readonly invalidFileTypeMessageSummary = input('Geçersiz belge türü: ');
  readonly invalidFileTypeMessageDetail = input('');
  readonly multiple = input(false);

  readonly fileContent = input('');
  readonly fileNameLabel = input('');
  readonly fileName = input('');
  readonly fileType = input('');

  readonly fileExtension = computed(() => {
    const name = this.fileName();
    if (!name) return '';
    const dotIdx = name.lastIndexOf('.');
    return dotIdx >= 0 ? name.substring(dotIdx + 1).toLowerCase() : '';
  });

  readonly canPreview = computed(() => {
    const mime = this.fileType();
    if (mime.startsWith('image/')) return true;

    const content = this.fileContent();
    if (content.startsWith('data:image/')) return true;

    const ext = this.fileExtension();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
  });

  readonly showFileRequest = output<void>();
  readonly fileSelect = output<FileSelectResult>();
  readonly clearRequest = output<void>();

  errorMessage = '';

  choose(): void {
    this.nativeInput()?.nativeElement.click();
  }

  onNativeChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const fileList = input.files;
    if (!fileList?.length) return;

    const files = Array.from(fileList);
    const maxBytes = this.maxFileSize() * 1_000_000;
    const accept = this.acceptedFileExtentions();

    for (const f of files) {
      if (f.size > maxBytes) {
        this.errorMessage = `${this.invalidFileTypeMessageSummary()} Maksimum: ${this.maxFileSize()} MB`;
        input.value = '';
        return;
      }
      if (accept && !this.isAccepted(f, accept)) {
        this.errorMessage = this.invalidFileTypeMessageDetail() || `${this.invalidFileTypeMessageSummary()} ${accept}`;
        input.value = '';
        return;
      }
    }

    this.errorMessage = '';
    this.fileSelect.emit({ originalEvent: event, currentFiles: files, files });
    input.value = '';
  }

  clear(): void {
    this.resetNativeInput();
    this.clearRequest.emit();
  }

  resetNativeInput(): void {
    this.errorMessage = '';
    const el = this.nativeInput()?.nativeElement;
    if (el) el.value = '';
  }

  private isAccepted(file: File, accept: string): boolean {
    const types = accept.split(',').map((t) => t.trim().toLowerCase());
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();

    return types.some((t) => {
      if (t.startsWith('.')) return fileName.endsWith(t);
      if (t.endsWith('/*')) return mimeType.startsWith(t.replace('/*', '/'));
      return mimeType === t;
    });
  }
}
